import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLeadModal } from '../context/LeadModalContext';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useLeadModal();
  const { user, loading, logout } = useAuth();
  const { selectedCountry, countriesList, changeCountry, getShortCode } = useCountry();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [zoomClass, setZoomClass] = useState('');
  const [logoError, setLogoError] = useState(false);

  // Handle browser zoom level detection
  useEffect(() => {
    const checkZoom = () => {
      const ratio = window.devicePixelRatio || 1;
      let base = 1;
      if (window.matchMedia && window.matchMedia('(min-resolution: 2dppx)').matches) {
        base = 2;
      }
      const relativeZoom = ratio / base;

      if (relativeZoom >= 1.9) {
        setZoomClass('zoom-200');
      } else if (relativeZoom >= 1.65) {
        setZoomClass('zoom-175');
      } else if (relativeZoom >= 1.4) {
        setZoomClass('zoom-150');
      } else if (relativeZoom >= 1.15) {
        setZoomClass('zoom-125');
      } else {
        setZoomClass('zoom-100');
      }
    };

    window.addEventListener('resize', checkZoom);
    const interval = setInterval(checkZoom, 1000);
    checkZoom();

    return () => {
      window.removeEventListener('resize', checkZoom);
      clearInterval(interval);
    };
  }, []);

  // Handle scroll listener for sticky class
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check once on mount

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleNavClick = (e, path) => {
    if (path.startsWith('#')) {
      e.preventDefault();
      const sectionId = path.replace('#', '');
      if (location.pathname === '/' || location.pathname === '/home') {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(`/${path}`);
      }
      setMobileMenuOpen(false);
    } else {
      setMobileMenuOpen(false);
    }
  };

  const handleActionClick = (e) => {
    const isTtcDetailsPage = location.pathname.startsWith('/courses/');

    if (isTtcDetailsPage) {
      e.preventDefault();
      const applySection = document.getElementById('apply');
      if (applySection) {
        applySection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      e.preventDefault();
      openModal();
    }
  };

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    navigate('/');
  };


  // Helper to check if a navigation link should have the "active" class
  const getNavLinkClass = (hashPath, normalPath) => {
    if (normalPath && location.pathname === normalPath) {
      return 'nav-link active';
    }
    // For home page ScrollSpy or simple active highlights
    if (hashPath && location.pathname === '/' && location.hash === hashPath) {
      return 'nav-link active';
    }
    // Default active home if on root and no hash
    if (hashPath === '#home' && location.pathname === '/' && !location.hash) {
      return 'nav-link active';
    }
    return 'nav-link';
  };

  const isTtcDetailsPage = location.pathname.startsWith('/courses/');
  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  return (
    <header id="header" className={isSticky || isTtcDetailsPage || location.pathname !== '/' ? 'sticky' : ''}>
      <div className="container">
        <Link to="/" className={`logo ${zoomClass}`} onClick={(e) => handleNavClick(e, '#home')}>
          <img 
            src={getLogoUrl()} 
            className={`brand-logo ${logoError ? 'logo-error' : ''}`} 
            alt="PranaFlow Academy" 
            height="54"
            style={{ height: '54px', maxHeight: '54px', width: 'auto' }}
            onError={() => setLogoError(true)} 
          />
          <div className="brand-text-container">
            <span className="brand-name">PranaFlow Academy</span>
            <span className="brand-tagline">Yoga • Wellness • Nutrition</span>
          </div>
        </Link>

        {mobileMenuOpen && (
          <div 
            className="mobile-nav-overlay" 
            onClick={() => setMobileMenuOpen(false)} 
          />
        )}

        <nav>
          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`} id="navMenu">
            <li>
              <a href="#home" className={getNavLinkClass('#home', '/')} onClick={(e) => handleNavClick(e, '#home')}>Home</a>
            </li>
            <li>
              <Link to="/about-us" className={getNavLinkClass(null, '/about-us')} onClick={() => setMobileMenuOpen(false)}>About</Link>
            </li>
            <li>
              <a href="#why-choose" className={getNavLinkClass('#why-choose')} onClick={(e) => handleNavClick(e, '#why-choose')}>Why Choose Us</a>
            </li>
            <li>
              <Link to="/courses" className={getNavLinkClass(null, '/courses')} onClick={() => setMobileMenuOpen(false)}>Courses</Link>
            </li>
            <li>
              <Link to="/teach-with-us" className={getNavLinkClass(null, '/teach-with-us')} onClick={() => setMobileMenuOpen(false)}>Teach With Us</Link>
            </li>
            <li>
              <a href="#team" className={getNavLinkClass('#team')} onClick={(e) => handleNavClick(e, '#team')}>Team</a>
            </li>
            <li>
              <a href="#faq" className={getNavLinkClass('#faq')} onClick={(e) => handleNavClick(e, '#faq')}>FAQ</a>
            </li>
            <li>
              <Link to="/contact-us" className={getNavLinkClass(null, '/contact-us')} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            </li>
            {user ? (
              <li>
                <Link to={`/${user.role}/dashboard`} className="nav-link" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
              </li>
            ) : (
              <li className="mobile-only-auth">
                <Link to="/login" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              </li>
            )}
            {user && (
              <li className="mobile-only-auth">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="nav-link"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit', textAlign: 'left', width: '100%' }}
                >
                  Logout
                </button>
              </li>
            )}
          </ul>
        </nav>

        <div className="header-right-actions">


          {/* Desktop Login / Logout button */}
          {!loading && (
            <div className="nav-action-btn desktop-auth-btn desktop-only-auth" style={{ marginLeft: '8px' }}>
              {user ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleLogout}
                  style={{ fontSize: '0.88rem', padding: '8px 18px' }}
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  className="btn btn-primary"
                  style={{ fontSize: '0.88rem', padding: '8px 18px' }}
                >
                  Login
                </Link>
              )}
            </div>
          )}

          {/* Desktop country selector */}
          <div className="desktop-country-selector">
            <button
              type="button"
              className="country-desktop-badge"
              onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
            >
              {getShortCode(selectedCountry.country_code)} ▾
            </button>
            {desktopDropdownOpen && (
              <>
                <div
                  className="desktop-country-dropdown-overlay"
                  onClick={() => setDesktopDropdownOpen(false)}
                />
                <ul className="desktop-country-dropdown-list">
                  {countriesList.map((c) => (
                    <li
                      key={c.country_code}
                      onClick={() => {
                        changeCountry(c.country_code);
                        setDesktopDropdownOpen(false);
                      }}
                    >
                      {c.currency_symbol} {c.country_name} ({c.currency_code})
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Mobile country selector */}
        <div className="mobile-country-selector">
          <button
            type="button"
            className="country-mobile-badge"
            onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
          >
            {getShortCode(selectedCountry.country_code)} ▾
          </button>
          {mobileDropdownOpen && (
            <>
              <div
                className="mobile-country-dropdown-overlay"
                onClick={() => setMobileDropdownOpen(false)}
              />
              <ul className="mobile-country-dropdown-list">
                {countriesList.map((c) => (
                  <li
                    key={c.country_code}
                    onClick={() => {
                      changeCountry(c.country_code);
                      setMobileDropdownOpen(false);
                    }}
                  >
                    {c.currency_symbol} {c.country_name} ({c.currency_code})
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <button
          className={`nav-toggle ${mobileMenuOpen ? 'active' : ''}`}
          id="navToggle"
          aria-label="Toggle menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default PublicHeader;
